import { useEffect, useState } from 'react';
import {
  Card,
  Text,
  Badge,
  IndexTable,
  EmptyState,
  Spinner,
  Banner,
  Box,
  Layout,
  BlockStack,
  InlineStack,
  Icon,
  Filters,
  ChoiceList,
  Select,
  Link,
  InlineGrid,
} from '@shopify/polaris';
import { 
  CalendarIcon,
  ProductIcon,
  InventoryIcon,
  CheckIcon,
} from '@shopify/polaris-icons';
import katanaApi, { KatanaApiError } from '@/services/katana';
import type { 
  ManufacturingOrder, 
  ManufacturingOrderStatus, 
  IngredientAvailability,
  Variant,
  Location,
  ManufacturingOrderFilters,
} from '@/types/katana';

interface DateRange {
  start: Date;
  end: Date;
}

export default function ManufacturingOrdersList() {
  const [manufacturingOrders, setManufacturingOrders] = useState<ManufacturingOrder[]>([]);
  const [variants, setVariants] = useState<Record<number, Variant>>({});
  const [locations, setLocations] = useState<Record<number, Location>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<ManufacturingOrderStatus[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [datePreset, setDatePreset] = useState<string>('this_month');

  // Date preset options
  const getDatePresets = (): Record<string, { label: string; start: Date | null; end: Date | null }> => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    return {
      'this_month': {
        label: 'This Month',
        start: new Date(year, month, 1),
        end: new Date(year, month + 1, 0, 23, 59, 59)
      },
      'last_month': {
        label: 'Last Month',
        start: new Date(year, month - 1, 1),
        end: new Date(year, month, 0, 23, 59, 59)
      },
      'this_quarter': {
        label: 'This Quarter',
        start: new Date(year, Math.floor(month / 3) * 3, 1),
        end: new Date(year, Math.floor(month / 3) * 3 + 3, 0, 23, 59, 59)
      },
      'last_quarter': {
        label: 'Last Quarter',
        start: new Date(year, Math.floor(month / 3) * 3 - 3, 1),
        end: new Date(year, Math.floor(month / 3) * 3, 0, 23, 59, 59)
      },
      'this_year': {
        label: 'This Year',
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31, 23, 59, 59)
      },
      'last_year': {
        label: 'Last Year',
        start: new Date(year - 1, 0, 1),
        end: new Date(year - 1, 11, 31, 23, 59, 59)
      },
      'last_30_days': {
        label: 'Last 30 Days',
        start: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        end: now
      },
      'last_90_days': {
        label: 'Last 90 Days',
        start: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
        end: now
      },
      'all_time': {
        label: 'All Time',
        start: null,
        end: null
      }
    };
  };

  useEffect(() => {
    // Set default date range to this month
    const presets = getDatePresets();
    const defaultPreset = presets[datePreset];
    if (defaultPreset.start && defaultPreset.end) {
      setDateRange({ start: defaultPreset.start, end: defaultPreset.end });
    } else {
      setDateRange(null);
    }
  }, []);

  useEffect(() => {
    fetchManufacturingOrders();
    fetchVariants();
    fetchLocations();
  }, [statusFilter, dateRange]);

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset);
    const presets = getDatePresets();
    const selectedPreset = presets[preset];
    
    if (selectedPreset.start && selectedPreset.end) {
      setDateRange({ start: selectedPreset.start, end: selectedPreset.end });
    } else {
      setDateRange(null);
    }
  };

  const fetchManufacturingOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build filters with date range and status
      const filters: ManufacturingOrderFilters = {};
      if (statusFilter.length > 0) {
        filters.status = statusFilter;
      }

      // Add date filtering
      let dateFrom: string | undefined;
      let dateTo: string | undefined;
      
      if (dateRange) {
        dateFrom = dateRange.start.toISOString();
        dateTo = dateRange.end.toISOString();
      }

      const response = await katanaApi.getManufacturingOrders(
        filters,
        { field: 'order_created_date', direction: 'desc' },
        1,
        500, // Increase limit since we're filtering by date
        dateFrom,
        dateTo
      );

      setManufacturingOrders(response.data);
    } catch (err) {
      console.error('Error fetching manufacturing orders:', err);
      setError(err instanceof KatanaApiError ? err.message : 'Failed to load manufacturing orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchVariants = async () => {
    try {
      const response = await katanaApi.getVariants(1, 100);
      const variantMap = response.data.reduce((acc, variant) => {
        acc[variant.id] = variant;
        return acc;
      }, {} as Record<number, Variant>);
      setVariants(variantMap);
    } catch (err) {
      console.error('Error fetching variants:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await katanaApi.getLocations();
      const locationMap = response.reduce((acc, location) => {
        acc[location.id] = location;
        return acc;
      }, {} as Record<number, Location>);
      setLocations(locationMap);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusBadge = (status: ManufacturingOrderStatus) => {
    switch (status) {
      case 'NOT_STARTED':
        return <Badge tone="info">Not Started</Badge>;
      case 'BLOCKED':
        return <Badge tone="critical">Blocked</Badge>;
      case 'IN_PROGRESS':
        return <Badge tone="warning">In Progress</Badge>;
      case 'DONE':
        return <Badge tone="success">Completed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getAvailabilityBadge = (availability: IngredientAvailability) => {
    switch (availability) {
      case 'IN_STOCK':
        return <Badge tone="success">In Stock</Badge>;
      case 'NOT_AVAILABLE':
        return <Badge tone="critical">Not Available</Badge>;
      case 'EXPECTED':
        return <Badge tone="warning">Expected</Badge>;
      case 'PROCESSED':
        return <Badge tone="info">Processed</Badge>;
      case 'NO_RECIPE':
        return <Badge tone="attention">No Recipe</Badge>;
      case 'NOT_APPLICABLE':
        return <Badge>Not Applicable</Badge>;
      default:
        return <Badge>{availability}</Badge>;
    }
  };

  const getTotalMetrics = () => {
    const totalOrders = manufacturingOrders.length;
    const totalValue = manufacturingOrders.reduce((sum, order) => sum + (order.total_cost || 0), 0);
    const overdueOrders = manufacturingOrders.filter(order => 
      order.production_deadline_date && 
      new Date(order.production_deadline_date) < new Date() && 
      order.status !== 'DONE'
    ).length;
    
    const notStartedPlannedQty = manufacturingOrders
      .filter(order => order.status === 'NOT_STARTED')
      .reduce((sum, order) => sum + order.planned_quantity, 0);

    return {
      totalOrders,
      totalValue,
      overdueOrders,
      notStartedPlannedQty,
    };
  };

  // Filter components
  const statusFilterOptions = [
    { label: 'Not Started', value: 'NOT_STARTED' },
    { label: 'Blocked', value: 'BLOCKED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'DONE' },
  ];

  const appliedFilters = [];
  if (statusFilter.length > 0) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${statusFilter.join(', ')}`,
      onRemove: () => setStatusFilter([]),
    });
  }
  if (dateRange) {
    const presets = getDatePresets();
    const currentPreset = Object.entries(presets).find(([_key, value]) => 
      value.start && value.end && dateRange.start && dateRange.end &&
      value.start.getTime() === dateRange.start.getTime() && 
      value.end.getTime() === dateRange.end.getTime()
    );
    const label = currentPreset ? currentPreset[1].label : 
      `${dateRange.start.toLocaleDateString()} - ${dateRange.end.toLocaleDateString()}`;
    appliedFilters.push({
      key: 'date',
      label: `Date: ${label}`,
      onRemove: () => {
        setDateRange(null);
        setDatePreset('all_time');
      },
    });
  }

  const datePresetOptions = Object.entries(getDatePresets()).map(([key, value]) => ({
    label: value.label,
    value: key,
  }));

  const filters = (
    <Filters
      queryValue={''}
      queryPlaceholder="Filter by status..."
      onQueryChange={() => {}} // Disabled search functionality
      onQueryClear={() => {}} // Disabled search functionality
      filters={[
        {
          key: 'status',
          label: 'Status',
          filter: (
            <ChoiceList
              title="Status"
              titleHidden
              choices={statusFilterOptions}
              selected={statusFilter}
              onChange={(selected) => setStatusFilter(selected as ManufacturingOrderStatus[])}
              allowMultiple
            />
          ),
          shortcut: true,
        },
        {
          key: 'date',
          label: 'Date Range',
          filter: (
            <BlockStack gap="200">
              <Select
                label="Date Range"
                labelHidden
                options={datePresetOptions}
                value={datePreset}
                onChange={handleDatePresetChange}
              />
              {dateRange && (
                <Text as="p" variant="bodySm" tone="subdued">
                  {dateRange.start.toLocaleDateString()} - {dateRange.end.toLocaleDateString()}
                </Text>
              )}
            </BlockStack>
          ),
          shortcut: true,
        },
      ]}
      appliedFilters={appliedFilters}
      onClearAll={() => {
        setStatusFilter([]);
        setDateRange(null);
        setDatePreset('all_time');
      }}
    />
  );

  if (loading) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="800">
              <BlockStack gap="400" align="center">
                <Spinner size="large" />
                <Text as="p" variant="bodyMd">
                  Loading manufacturing orders...
                </Text>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Layout.Section>
          <Banner tone="critical" title="Unable to load manufacturing orders">
            <p>{error}</p>
            <p>
              Please check your Katana API configuration in{' '}
              <a href="/settings">Settings</a>.
            </p>
          </Banner>
        </Layout.Section>
      </Layout>
    );
  }

  if (manufacturingOrders.length === 0) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <EmptyState
              heading="No manufacturing orders found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>There are currently no manufacturing orders matching your criteria.</p>
            </EmptyState>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  const metrics = getTotalMetrics();

  // Get current date range display text
  const getDateRangeText = () => {
    if (!dateRange) return 'All time';
    const presets = getDatePresets();
    const currentPreset = Object.entries(presets).find(([_key, value]) => 
      value.start && value.end && dateRange.start && dateRange.end &&
      value.start.getTime() === dateRange.start.getTime() && 
      value.end.getTime() === dateRange.end.getTime()
    );
    return currentPreset ? currentPreset[1].label : 
      `${dateRange.start.toLocaleDateString()} - ${dateRange.end.toLocaleDateString()}`;
  };

  return (
    <>
      {/* Page header card for development mode */}
      <Card>
        <Box padding="400">
          <BlockStack gap="200">
            <InlineStack gap="200" align="space-between" blockAlign="center">
              <BlockStack gap="100">
                <Text as="h1" variant="headingLg">Manufacturing Orders</Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  Track and manage your production orders • {getDateRangeText()}
                </Text>
              </BlockStack>
            </InlineStack>
          </BlockStack>
        </Box>
      </Card>
      
      <Layout>
        {/* Metrics Cards */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <InlineGrid columns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }} gap="400">
                <BlockStack gap="200" align="center">
                  <Icon source={ProductIcon} tone="base" />
                  <Text as="h3" variant="headingMd" tone="subdued" alignment="center">
                    Total Orders
                  </Text>
                  <Text as="p" variant="heading2xl" alignment="center">
                    {metrics.totalOrders}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                    Manufacturing orders
                  </Text>
                </BlockStack>
                <BlockStack gap="200" align="center">
                  <Icon source={InventoryIcon} tone="base" />
                  <Text as="h3" variant="headingMd" tone="subdued" alignment="center">
                    Total Value
                  </Text>
                  <Text as="p" variant="heading2xl" alignment="center">
                    {formatCurrency(metrics.totalValue)}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                    Planned production cost
                  </Text>
                </BlockStack>
                <BlockStack gap="200" align="center">
                  <Icon source={CalendarIcon} tone={metrics.overdueOrders > 0 ? 'critical' : 'success'} />
                  <Text as="h3" variant="headingMd" tone="subdued" alignment="center">
                    Overdue Orders
                  </Text>
                  <Text
                    as="p"
                    variant="heading2xl"
                    alignment="center"
                    tone={metrics.overdueOrders > 0 ? 'critical' : 'success'}
                  >
                    {metrics.overdueOrders}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                    {metrics.overdueOrders > 0 ? 'Need attention' : 'All on time'}
                  </Text>
                </BlockStack>
                <BlockStack gap="200" align="center">
                  <Icon source={CheckIcon} tone="info" />
                  <Text as="h3" variant="headingMd" tone="subdued" alignment="center">
                    Not Started Qty
                  </Text>
                  <Text as="p" variant="heading2xl" alignment="center">
                    {metrics.notStartedPlannedQty}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                    Planned quantity
                  </Text>
                </BlockStack>
              </InlineGrid>
            </Box>
          </Card>
        </Layout.Section>

        {/* Orders Table */}
        <Layout.Section>
          <Card>
            <Box padding="400" paddingBlockEnd="400">
              {filters}
            </Box>
            <IndexTable
              resourceName={{
                singular: 'manufacturing order',
                plural: 'manufacturing orders',
              }}
              itemCount={manufacturingOrders.length}
              headings={[
                { title: 'Order #' },
                { title: 'Product' },
                { title: 'Status' },
                { title: 'Availability' },
                { title: 'Planned Qty', alignment: 'end' },
                { title: 'Total Cost', alignment: 'end' },
                { title: 'Deadline' },
                { title: 'Location' },
              ]}
              selectable={false}
            >
              {manufacturingOrders.map((order, index) => (
                <IndexTable.Row
                  id={String(order.id)}
                  key={order.id}
                  position={index}
                >
                  <IndexTable.Cell>
                    <Link
                      url={`https://factory.katanamrp.com/manufacturingorder/${order.id}`}
                      target="_blank"
                      removeUnderline
                    >
                      {order.order_no}
                    </Link>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {variants[order.variant_id]?.sku || `Variant ${order.variant_id}`}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {getStatusBadge(order.status)}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {getAvailabilityBadge(order.ingredient_availability)}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" alignment="end" numeric>
                      {order.planned_quantity}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" alignment="end" numeric>
                      {formatCurrency(order.total_cost || 0)}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {order.production_deadline_date
                      ? new Date(order.production_deadline_date).toLocaleDateString()
                      : 'No deadline'}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {locations[order.location_id]?.name || `Location ${order.location_id}`}
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>
    </>
  );
}